import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import {
  AsyncErrorBoundary,
  DefaultErrorFallback,
} from "@norkron/web-error-boundary";
import { TooltipProvider } from "@norkron/web-tooltip";
import { App } from "./App";
import { AuthProvider } from "./auth/AuthContext";
import { ToastProvider } from "./compose/Toast";
import { SyncProvider } from "./sync/SyncProvider";
import { ThemeProvider } from "./theme/ThemeProvider";

// Token layers first (order matters: canonical base → elaborate overrides),
// then chrome + screen styles.
//
// The base layer is the LIB's own stylesheet, not a copy of it. It used to be
// `./theme/norkron-base.css`, a vendored snapshot of this exact file taken at
// web-themer v0.14.0 and left to rot: MEASURED 66 custom properties against the
// real file's 204 (66 vs 101 in the `--norkron-*` namespace alone).
// Every token it had lost silently degraded to whatever literal fallback each
// consumer lib happened to write at its reference site — which is how the live
// Download menu and Articles row menu came to paint `.menu__item:hover` with
// `var(--norkron-bg-muted, #eef2f7)` = near-white behind near-white dark-theme
// text (measured 1.01:1). A vendored token layer cannot be kept in sync by
// discipline; importing the package is the only version of this that stays true.
import "@norkron/web-themer/base.css";
import "./theme/ela-theme.css";
// Alexandria (self-hosted) overrides the sans/serif font tokens — imported after
// ela-theme so its :root declarations win.
import "./styles/fonts.css";
import "./styles/chrome.css";
// Avatar shape-by-actor-type + deterministic fallback fill (after chrome.css so
// it layers on the base `.avatar`).
import "./styles/avatar.css";
import "./styles/screens.css";
import "./styles/settings.css";
import "./styles/annotations.css";
import "./styles/tickets.css";
import "./styles/kanban.css";
import "./styles/export-versioning.css";
import "./styles/offline.css";
// Article-workbench pane/stage chrome (app-owned) + the composed lib styles for
// the review-workbench primitives (stage/pills/timeline/diff) and the research
// markdown editor. The lib CSS is imported by relative dist path (the same
// resolution the vite aliases use) so no package-subpath export is needed.
import "./styles/workbench.css";
import "../../../../libs/frontend/web/web-review-workbench/dist/styles.css";
import "../../../../libs/frontend/web/web-markdown-editor/dist/styles.css";
// The WYSIWYG editor's formatting-toolbar chrome (`.ela-toolbar` / `.ela-icon` /
// `.ela-editor-shell`). Without it the edit-mode toolbar renders as unstyled
// controls — same relative-dist resolution as the vite alias.
import "../../../../libs/frontend/web/web-elaborate-editor/dist/styles.css";
// The dual-mode <ToolbarShell> (web-elaborate-editor ≥0.5.1) composes these
// web-* primitives — button groups / tabs / segmented controls (ribbon + simple
// toolbar) AND menu dropdowns / popovers (menu bar, ⋯ overflow, colour·table·link
// pickers). Import every composed primitive's chrome so the toolbar renders as a
// styled ribbon / menu bar rather than bare controls. web-menu + web-popover DO
// ship stylesheets (dist/index.css) — they were previously (incorrectly) omitted,
// which left the menu bar + pickers unstyled. Only the editor's ToolbarShell uses
// web-menu / web-popover in this app, so these imports have no other blast radius.
// ⛔ web-sidebar's STATE CSS — the app shell's nav rail. Unlike most of the libs
// here it self-injects NOTHING (`sideEffects: false`), so without this line
// `sidebar__item--active` is a class with no rule behind it: the markup is
// byte-identical and the user loses every visual cue of which page they are on.
// That was the defect web-sidebar 0.4.0 fixed by shipping this file, and the fix
// only lands in a consumer that imports it. `chrome.css` binds the lib's
// `--sidebar__*` channels; its overrides are written as `.shell__sidebar …`
// compounds so they outrank these plain-class rules regardless of import order.
import "../../../../libs/frontend/web/web-sidebar/dist/index.css";
import "../../../../libs/frontend/web/web-controls/dist/styles.css";
import "../../../../libs/frontend/web/web-tabs/dist/styles.css";
import "../../../../libs/frontend/web/web-segmented-control/dist/styles.css";
// The privz access console's own BEM stylesheet (`/w/:slug/admin` → Access).
// ⚠️ Its `@norkron/web-admin-shell` dependency needs no import — v0.5.0+ injects
// its 31 KB stylesheet through a layout effect. Before that shipped, eleven of
// this console's components emitted Tailwind utility strings into a product with
// no Tailwind and rendered completely unstyled.
import "../../../../libs/frontend/web/web-privileges-admin/dist/privileges-admin.css";
import "../../../../libs/frontend/web/web-menu/dist/index.css";
import "../../../../libs/frontend/web/web-popover/dist/index.css";
// web-tooltip styles the label tips web-controls renders on every toolbar icon
// button (its --tooltip-* tokens are bridged onto --norkron-* by the editor's
// styles.css, imported above).
import "../../../../libs/frontend/web/web-tooltip/dist/styles.css";
// The ReactionBar `+` picker composes @norkron/web-emoji-picker — its BEM
// stylesheet is token-driven (light+dark via prefers-color-scheme + data-theme,
// falling back to a legible --ep-* layer when no themer semantic tokens exist).
import "../../../../libs/frontend/web/web-emoji-picker/dist/styles.css";
// The crash-card chrome. `DefaultErrorFallback` ships semantic BEM classes and no
// styles of its own, so without this the fallback renders as unstyled stacked
// text — which reads to a client as a broken page rather than a handled error.
// Its `--error-boundary-*` token surface is bridged onto `--norkron-*` in
// ela-theme.css so the card follows the theme (the lib's own defaults are
// light-only).
import "../../../../libs/frontend/web/web-error-boundary/dist/styles.css";

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    {/*
      ⛔ THE LAST LINE OF DEFENCE.

      Until this landed, `grep -rn "ErrorBoundary|componentDidCatch" src`
      returned ZERO hits across 79k LOC. React unmounts the entire tree when a
      render error reaches the root uncaught, so any throw anywhere — including
      inside these providers — replaced the whole client-facing SPA with a blank
      white page, with no message, nothing to quote to support, and no recovery
      but a manual reload.

      `AsyncErrorBoundary` rather than `ErrorBoundary`: the root is the only place
      that can catch an unhandled promise REJECTION, and nearly every elaborate
      screen is an async data load. A plain boundary here would miss all of them.

      Placed OUTSIDE the providers on purpose. A crash in AuthProvider or
      SyncProvider is exactly the case where the fallback still has to render, so
      the boundary cannot depend on them. `DefaultErrorFallback` needs nothing
      from this tree — its theme values come from web-themer's module-level store,
      not from React context.

      Per-route containment lives in App.tsx's <RouteBoundary>, which keeps the
      shell alive when a single screen fails. This root boundary is only reached
      when the shell itself is what broke.
    */}
    <AsyncErrorBoundary
      fallback={(error, reset) => (
        <div className="page" data-testid="app-error">
          <DefaultErrorFallback
            error={error}
            reset={reset}
            title="Elabor8 ran into a problem"
          />
        </div>
      )}
    >
      <BrowserRouter>
        <ThemeProvider>
          {/*
          Tooltip placement policy for the WHOLE app, declared once.

          `axis="vertical"` is a hard constraint, not a preference: every
          nk-tooltip-content renders ABOVE or BELOW its trigger and never to
          the left or right. It holds at viewport edges (the flip stays on the
          vertical axis) and in the degenerate case where neither side fits
          (the tooltip shifts along the cross axis rather than escaping
          sideways). Enforced in @norkron/web-floating's computePosition via
          `allowedSides`, so it cannot be defeated by a collision.

          `sideOffset="0.2rem"` is the trigger→tooltip gap. It stays in rem so
          it tracks the root font-size (user text-size preference) instead of
          freezing at 3.2px.

          This sits at the ROOT because the tooltips are rendered inside
          web-controls' Button/IconButton, not by app code — there is no
          per-call-site prop to set. See the web-tooltip alias in
          vite.config.ts for why the context reaches them.
        */}
          <TooltipProvider axis="vertical" sideOffset="0.2rem">
            <ToastProvider>
              <AuthProvider>
                <SyncProvider>
                  <App />
                </SyncProvider>
              </AuthProvider>
            </ToastProvider>
          </TooltipProvider>
        </ThemeProvider>
      </BrowserRouter>
    </AsyncErrorBoundary>
  </StrictMode>,
);
